import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Shield, Search, Scale, Building2, Users, ChevronRight,
  ArrowRight, Eye, FileCheck, Gavel, HeartPulse, Lock,
  Briefcase, Globe, ChevronDown, Menu, X
} from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9eb2c609-62e3-467e-b17e-230b32f56602/artifacts/8wo2hyy0_eZamindar%20LoGo.PNG";
const HERO_BG = "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBsdXh1cnklMjB0ZXh0dXJlJTIwZ29sZHxlbnwwfHx8fDE3NzYwMTE2NjN8MA&ixlib=rb-4.1.0&q=85";
const MANSION_IMG = "https://images.unsplash.com/photo-1758612853656-def5033bccb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBtYW5zaW9uJTIwZXZlbmluZ3xlbnwwfHx8fDE3NzYwMTE2NTR8MA&ixlib=rb-4.1.0&q=85";
const ADVISOR_IMG = "https://images.unsplash.com/photo-1771244688590-1e481dba1b5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGFkdmlzb3IlMjBvZmZpY2V8ZW58MHx8fHwxNzc2MDExNjU0fDA&ixlib=rb-4.1.0&q=85";

const teamMembers = [
  { name: "Vaibhav Jain", role: "Founder & Property Intelligence Framework Chief", desc: "Leads the vision and strategic direction of eZamindar with hands-on experience in real estate transactions, title complexities, and dispute resolution." },
  { name: "Nagarjuna Kinare", role: "Co-Founder & CTO", desc: "Oversees the technology architecture, leading development of scalable systems for property data structuring, intelligence processing, and platform automation." },
  { name: "Dhruv Chawla", role: "Director – PIF Technology", desc: "Responsible for translating the Property Intelligence Framework into practical technological workflows and systems." },
  { name: "Sahaj Sandhu", role: "Advisor – Product", desc: "Provides strategic guidance on product development, focusing on user experience, product-market fit, and scalable design thinking." },
  { name: "Jason Fernandes", role: "Advisor – Tokenomics & Web3", desc: "Advises on future integration of tokenomics and decentralized frameworks for Web3-enabled property intelligence." },
  { name: "Dr. Shravani Shahapure", role: "Advisor – Cryptography", desc: "Contributes to data integrity, security protocols, and trust frameworks within the platform." },
  { name: "Raj Kapoor", role: "Advisor – Founder, India Blockchain Alliance", desc: "Provides strategic direction on blockchain integration, ecosystem positioning, and institutional alignment." },
];

const howItWorks = [
  { icon: Building2, title: "Register Your Property", desc: "Add your properties to the eZamindar intelligence platform with complete documentation." },
  { icon: Search, title: "Intelligence Assessment", desc: "Our certified PIOs conduct thorough investigations into ownership, possession, and documentation." },
  { icon: Eye, title: "Continuous Monitoring", desc: "Dedicated intelligence layer ensures no critical fact remains undiscovered about your property." },
  { icon: Shield, title: "Protection & Resolution", desc: "Preventive intelligence system protects ownership through verified truth and structured resolution." },
];

const services = [
  { icon: FileCheck, title: "Title Reports", desc: "Comprehensive title verification and intelligence reports for property ownership clarity." },
  { icon: Gavel, title: "Court Case Tracking", desc: "Real-time monitoring of all litigation and legal proceedings related to your properties." },
  { icon: HeartPulse, title: "Valuation Health", desc: "Ongoing property valuation monitoring with market-aligned health assessments." },
  { icon: Scale, title: "Legal Assistance SOS", desc: "Immediate access to legal support for property emergencies and dispute resolution." },
  { icon: Lock, title: "PIO Intelligence", desc: "Dedicated Property Intelligence Officer for monitoring, investigation, and risk prevention." },
  { icon: Globe, title: "Revenue Management", desc: "Track income, expenses, and financial health across all your property assets." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const goToDashboard = () => {
    if (user) navigate("/dashboard");
    else navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Header */}
      <header data-testid="landing-header" className="fixed top-0 w-full z-50 bg-[#050505]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="eZamindar" className="w-10 h-10 object-contain" />
            <span className="font-serif text-xl tracking-tight">
              e<span className="text-[#D4AF37]">Z</span>amindar
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">About</a>
            <a href="#how-it-works" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">How it Works</a>
            <a href="#services" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">Services</a>
            <a href="#team" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">Team</a>
            <a href="#careers" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">Careers</a>
          </nav>
          <div className="hidden md:flex items-center gap-4">
            <button
              data-testid="header-login-btn"
              onClick={() => navigate("/login")}
              className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors"
            >
              Sign In
            </button>
            <button
              data-testid="header-cta-btn"
              onClick={goToDashboard}
              className="bg-[#D4AF37] text-black font-semibold text-sm py-2 px-5 rounded-sm hover:bg-[#F59E0B] transition-colors"
            >
              Get Started
            </button>
          </div>
          <button className="md:hidden text-white/60" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0C0D0B] border-t border-white/10 px-4 py-4 space-y-3">
            <a href="#about" className="block text-white/60 hover:text-[#D4AF37] text-sm">About</a>
            <a href="#how-it-works" className="block text-white/60 hover:text-[#D4AF37] text-sm">How it Works</a>
            <a href="#services" className="block text-white/60 hover:text-[#D4AF37] text-sm">Services</a>
            <a href="#team" className="block text-white/60 hover:text-[#D4AF37] text-sm">Team</a>
            <a href="#careers" className="block text-white/60 hover:text-[#D4AF37] text-sm">Careers</a>
            <button onClick={goToDashboard} className="w-full bg-[#D4AF37] text-black font-semibold text-sm py-2 px-5 rounded-sm">Get Started</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] mb-6 font-sans font-medium animate-fadeInUp">
              Property Intelligence Framework
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-8 animate-fadeInUp animate-delay-1">
              Protect Your Property.<br />
              <span className="gold-gradient">Discover the Truth.</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-xl leading-relaxed mb-10 font-sans animate-fadeInUp animate-delay-2">
              eZamindar is an intelligent property intelligence platform connecting property owners with certified Property Intelligence Officers. Safeguard your assets with verified truth and structured resolution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animate-delay-3">
              <button
                data-testid="hero-cta-btn"
                onClick={goToDashboard}
                className="bg-[#D4AF37] text-black font-semibold py-3 px-8 rounded-sm hover:bg-[#F59E0B] transition-colors flex items-center justify-center gap-2"
              >
                Access Your Dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#about"
                data-testid="hero-learn-more-btn"
                className="border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] font-medium py-3 px-8 rounded-sm transition-colors flex items-center justify-center gap-2"
              >
                Learn More
                <ChevronDown className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About / What is eZamindar */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">The Problem</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
                Property Ownership is Under <span className="text-[#D4AF37]">Constant Threat</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-6 font-sans">
                Property disputes remain one of the most complex and prolonged challenges in real estate. Hidden title defects, forged documents, encroachment, and unresolved litigation silently erode the value and security of your most critical assets.
              </p>
              <p className="text-white/60 text-base leading-relaxed mb-8 font-sans">
                eZamindar is the answer: an intelligent, AI-powered property intelligence platform that deploys certified Property Intelligence Officers to monitor, investigate, and protect your properties. Think of it as a dedicated intelligence layer between your ownership and potential disputes.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: "500+", label: "Properties Protected" },
                  { num: "98%", label: "Dispute Prevention Rate" },
                  { num: "50+", label: "Certified PIOs" },
                  { num: "24/7", label: "Intelligence Monitoring" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-[#11130F] border border-white/10 p-4 rounded-sm">
                    <div className="font-serif text-2xl text-[#D4AF37] mb-1">{stat.num}</div>
                    <div className="text-white/50 text-xs uppercase tracking-wider font-sans">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={MANSION_IMG} alt="Property" className="w-full rounded-sm border border-white/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-sm" />
              <div className="absolute bottom-6 left-6 right-6 bg-[#11130F]/90 backdrop-blur-sm border border-white/10 rounded-sm p-4">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Our Mission</p>
                <p className="text-white/80 text-sm font-sans">
                  Ensuring property decisions are made with complete awareness, and ownership remains protected through verified truth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 bg-[#0C0D0B] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">Process</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
              How eZamindar <span className="text-[#D4AF37]">Works</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <div key={step.title} className="bg-[#11130F] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors group">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <span className="text-white/30 text-sm font-sans">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-lg text-white mb-2">{step.title}</h3>
                <p className="text-white/50 text-sm font-sans leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" data-testid="services-section" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">Intelligence Services</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
                Complete Property <span className="text-[#D4AF37]">Protection</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed font-sans mb-8">
                From title verification to court case tracking, our AI-powered dashboard gives you full visibility and control over every aspect of your property portfolio.
              </p>
              <button
                data-testid="services-cta-btn"
                onClick={goToDashboard}
                className="bg-[#D4AF37] text-black font-semibold py-3 px-6 rounded-sm hover:bg-[#F59E0B] transition-colors flex items-center gap-2"
              >
                Explore Dashboard
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((svc) => (
                <div key={svc.title} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                  <svc.icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h4 className="font-serif text-base text-white mb-2">{svc.title}</h4>
                  <p className="text-white/50 text-xs font-sans leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PIO Section */}
      <section className="py-24 md:py-32 bg-[#0C0D0B] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src={ADVISOR_IMG} alt="Professional Advisor" className="w-full rounded-sm border border-white/10" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0C0D0B] via-transparent to-transparent rounded-sm" />
            </div>
            <div>
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">Property Intelligence Officer</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
                Your Dedicated <span className="text-[#D4AF37]">Intelligence Layer</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-6 font-sans">
                A Property Intelligence Officer is not a conventional advisor. They are selected and certified professionals with investigative rigor, analytical discipline, and commitment to uncovering factual truth about your property.
              </p>
              <div className="space-y-4">
                {[
                  "Monitors status and integrity of your property",
                  "Identifies threats, inconsistencies, and risk indicators",
                  "Conducts lawful investigations into ownership and documentation",
                  "Supports structured resolution through factual clarity",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 shrink-0" />
                    <p className="text-white/60 text-sm font-sans">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" data-testid="team-section" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">Leadership</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
              The Core <span className="text-[#D4AF37]">Team</span>
            </h2>
            <p className="text-white/50 text-sm mt-4 font-sans max-w-lg mx-auto">
              A blend of property intelligence expertise, technology leadership, product strategy, and advanced digital infrastructure capabilities.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.map((member) => (
              <div key={member.name} className="bg-[#11130F] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
                <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mb-4">
                  <span className="text-[#D4AF37] font-serif text-lg">{member.name.charAt(0)}</span>
                </div>
                <h4 className="font-serif text-lg text-white mb-1">{member.name}</h4>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">{member.role}</p>
                <p className="text-white/50 text-sm font-sans leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" data-testid="careers-section" className="py-24 md:py-32 bg-[#0C0D0B] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] mb-4 font-sans">Careers</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
                Join the Intelligence <span className="text-[#D4AF37]">Network</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-8 font-sans">
                We are building a curated community of high-calibre individuals. If you possess investigative curiosity, analytical discipline, and a commitment to factual truth, the eZamindar network is your platform.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { title: "Property Intelligence Officer", location: "Pan India", type: "Contract" },
                { title: "Senior Full-Stack Developer", location: "Remote", type: "Full-time" },
                { title: "Legal Research Analyst", location: "Mumbai / Bangalore", type: "Full-time" },
                { title: "Product Designer", location: "Remote", type: "Full-time" },
              ].map((job) => (
                <div key={job.title} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between group cursor-pointer">
                  <div>
                    <h4 className="font-sans font-medium text-white group-hover:text-[#D4AF37] transition-colors">{job.title}</h4>
                    <p className="text-white/40 text-sm font-sans">{job.location} &middot; {job.type}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* eZAssure Section */}
      <section id="ezassure" data-testid="ezassure-section" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
                <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-sans">Introducing</p>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
                eZ<span className="text-[#D4AF37]">Assure</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-6 font-sans">
                Title Protection & Legal Risk Assurance. A structured verification and continuous monitoring program that proactively safeguards your property ownership through verified truth and coordinated legal response support.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Title clarity assessment with risk-based assurance",
                  "Continuous monitoring for mutations, court filings & encroachments",
                  "Legal response drafting & lawyer coordination",
                  "Immutable evidence vault for critical documents",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 shrink-0" />
                    <p className="text-white/60 text-sm font-sans">{item}</p>
                  </div>
                ))}
              </div>
              <button
                data-testid="ezassure-cta-btn"
                onClick={goToDashboard}
                className="bg-[#D4AF37] text-black font-semibold py-3 px-6 rounded-sm hover:bg-[#F59E0B] transition-colors flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Activate eZAssure
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { price: "4,999", plan: "Basic", features: "Quarterly monitoring, Title assessment, 5-doc vault" },
                { price: "9,999", plan: "Standard", features: "Monthly monitoring, Legal drafts, Case tracking, 25-doc vault" },
                { price: "24,999", plan: "Premium", features: "Continuous monitoring, Lawyer assignment, Unlimited vault, Priority support" },
              ].map((p, i) => (
                <div key={p.plan} className={`bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors ${i === 2 ? "col-span-2" : ""}`}>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-2">{p.plan}</p>
                  <p className="font-serif text-2xl text-white mb-2">INR {p.price}<span className="text-white/30 text-sm">/yr</span></p>
                  <p className="text-white/40 text-xs font-sans">{p.features}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6">
            Ready to <span className="text-[#D4AF37]">Protect</span> Your Properties?
          </h2>
          <p className="text-white/50 text-base font-sans max-w-lg mx-auto mb-10">
            Join hundreds of property owners who trust eZamindar to safeguard their assets with intelligence-driven protection.
          </p>
          <button
            data-testid="cta-get-started-btn"
            onClick={goToDashboard}
            className="bg-[#D4AF37] text-black font-semibold py-3 px-10 rounded-sm hover:bg-[#F59E0B] transition-colors inline-flex items-center gap-2"
          >
            Get Started Now
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="landing-footer" className="border-t border-white/10 py-12 bg-[#0C0D0B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="eZamindar" className="w-10 h-10 object-contain" />
                <span className="font-serif text-xl tracking-tight">
                  e<span className="text-[#D4AF37]">Z</span>amindar
                </span>
              </div>
              <p className="text-white/40 text-sm font-sans max-w-sm leading-relaxed">
                Smart Property Ledger & Title Intelligence Platform. Confidential. Lawful. Evidence-Driven.
              </p>
            </div>
            <div>
              <h4 className="text-white/60 text-xs uppercase tracking-wider font-sans mb-4">Platform</h4>
              <div className="space-y-2">
                <a href="#about" className="block text-white/40 hover:text-[#D4AF37] text-sm transition-colors">About</a>
                <a href="#services" className="block text-white/40 hover:text-[#D4AF37] text-sm transition-colors">Services</a>
                <a href="#careers" className="block text-white/40 hover:text-[#D4AF37] text-sm transition-colors">Careers</a>
              </div>
            </div>
            <div>
              <h4 className="text-white/60 text-xs uppercase tracking-wider font-sans mb-4">Legal</h4>
              <div className="space-y-2">
                <span className="block text-white/40 text-sm">Privacy Policy</span>
                <span className="block text-white/40 text-sm">Terms of Service</span>
                <span className="block text-white/40 text-sm">Security</span>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center">
            <p className="text-white/30 text-xs font-sans">
              &copy; {new Date().getFullYear()} eZamindar Property Intelligence Framework. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
